// =============================================
// musicLibrary — data helpers for the Music service
// =============================================
// Reads/writes the mus_* tables through dataAccess (API or localStorage). Resolves
// artist/album rows on upload (find-or-create), joins ownership → track metadata
// for the library view, and logs plays into mus_recently_played.
//
// PK columns are passed explicitly because dataAccess.guessIdColumn doesn't know
// the mus_* tables (it would guess wrong, e.g. mus_tracks_all → mus_tracks_all_id).
// =============================================
import { getTableData, loadTable as dbLoadTable } from '../db-viewer/data/dataAccess';
import { loadTable, addRow, saveRow } from './musicApi';
import { deleteAudio, getAudioBlob, isIdbKey } from '../../lib/audioStore';
import { putServerBlob as putServerBlobBinary } from '../../lib/serverBlobStore';

export const MUS_TRACK_PK = 'track_id';
export const MUS_OWNED_PK = 'user_owned_id';
export const MUS_ARTIST_PK = 'artist_id';
export const MUS_ALBUM_PK = 'album_id';
export const MUS_RECENT_PK = 'recently_played_id';

export interface TrackView {
  userOwnedId: number;
  trackId: number;
  title: string;
  artist: string;
  artistId: number | null;
  album: string;
  albumId: number | null;
  durationSeconds: number;
  fileFormat: string;
  fileSizeBytes: number;
  mediaType: string;       // 'audio' | 'video'
  storageKey: string;      // idb://… (or a remote URL in production)
  coverKey: string | null; // idb://… for the album art image, or null
  isFavorite: boolean;
  rating: number;          // 0 = unrated, 1-5 stars
  languageId: number | null;
  moodId: number | null;   // user mood overrides track mood
  playCount: number;       // catalog play_count (see getMostPlayed for per-user)
  addedAt: string;
}

export interface TrackGroup {
  key: string;
  label: string;
  count: number;
  tracks: TrackView[];
}

function nowTimestamp(): string {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
}

/** Current logged-in user id from the session, or null. */
export function getCurrentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = JSON.parse(raw).id;
    return id != null ? Number(id) : null;
  } catch { return null; }
}

/** Warm the local cache for everything the library/upload needs. */
export async function loadMusicTables(): Promise<void> {
  await Promise.all([
    loadTable('mus_tracks_all'),
    loadTable('mus_user_owned'),
    loadTable('mus_artist'),
    loadTable('mus_album'),
    loadTable('mus_mood'),
    loadTable('mus_recently_played'),
    loadTable('mus_wishlist'),
    loadTable('mus_playlist'),
    loadTable('mus_playlist_song'),
    // Non-music reference/identity tables stay on the generic dataAccess path
    // (DB for admins, localStorage otherwise) — they aren't in the music whitelist.
    dbLoadTable('site_mstr_languages'),
    dbLoadTable('users'),
    dbLoadTable('site_service_plans'),
  ]);
}

// ---- Find-or-create artist / album ----
export async function resolveArtistId(name: string): Promise<number | null> {
  const clean = (name || '').trim();
  if (!clean) return null;
  const existing = getTableData('mus_artist').find((a: any) =>
    a.is_deleted != 1 && (a.artist_name || '').trim().toLowerCase() === clean.toLowerCase()); // eslint-disable-line eqeqeq
  if (existing) return Number(existing.artist_id);
  const row = await addRow('mus_artist', {
    artist_name: clean,
    artist_type: 'Unknown',
    is_active: 1, is_deleted: 0,
    created_at: nowTimestamp(), updated_at: nowTimestamp(),
  }, MUS_ARTIST_PK);
  return Number(row[MUS_ARTIST_PK]);
}

export async function resolveAlbumId(name: string, artistId: number | null): Promise<number | null> {
  const clean = (name || '').trim();
  if (!clean) return null;
  const existing = getTableData('mus_album').find((a: any) =>
    // eslint-disable-next-line eqeqeq
    a.is_deleted != 1 &&
    (a.album_name || '').trim().toLowerCase() === clean.toLowerCase() &&
    // eslint-disable-next-line eqeqeq
    (artistId == null || a.artist_id == artistId));
  if (existing) return Number(existing.album_id);
  const row = await addRow('mus_album', {
    album_name: clean,
    artist_id: artistId,
    album_type: 'Album',
    is_active: 1, is_deleted: 0,
    created_at: nowTimestamp(), updated_at: nowTimestamp(),
  }, MUS_ALBUM_PK);
  return Number(row[MUS_ALBUM_PK]);
}

// ---- Library read (ownership ⨝ track ⨝ artist ⨝ album) ----
export function getOwnedTracks(userId: number): TrackView[] {
  const tracks = getTableData('mus_tracks_all');
  const artists = getTableData('mus_artist');
  const albums = getTableData('mus_album');
  const trackById = new Map<number, any>(tracks.map((t: any) => [Number(t.track_id), t]));
  const artistById = new Map<number, any>(artists.map((a: any) => [Number(a.artist_id), a]));
  const albumById = new Map<number, any>(albums.map((a: any) => [Number(a.album_id), a]));

  const out: TrackView[] = [];
  for (const o of getTableData('mus_user_owned')) {
    // eslint-disable-next-line eqeqeq
    if (o.user_id != userId || o.is_deleted == 1 || o.is_active == 0) continue;
    const t = trackById.get(Number(o.track_id));
    // eslint-disable-next-line eqeqeq
    if (!t || t.is_deleted == 1) continue;
    const artist = artistById.get(Number(t.artist_id));
    const album = albumById.get(Number(t.album_id));
    out.push({
      userOwnedId: Number(o.user_owned_id),
      trackId: Number(t.track_id),
      title: t.track_title || o.original_filename || 'Untitled',
      artist: artist?.artist_name || t.id3_artist || 'Unknown Artist',
      artistId: t.artist_id != null ? Number(t.artist_id) : null,
      album: album?.album_name || t.id3_album || '',
      albumId: t.album_id != null ? Number(t.album_id) : null,
      durationSeconds: Number(t.duration_seconds) || 0,
      fileFormat: t.file_format || '',
      fileSizeBytes: Number(t.file_size_bytes) || 0,
      mediaType: t.media_type || 'audio',
      storageKey: t.storage_path,
      coverKey: (t.id3_raw && t.id3_raw.cover_key) ? t.id3_raw.cover_key : null,
      isFavorite: o.is_favorite == 1, // eslint-disable-line eqeqeq
      rating: Number(o.user_rating) || 0,
      languageId: t.language_id != null ? Number(t.language_id) : null,
      moodId: (o.user_mood_id != null ? Number(o.user_mood_id) : (t.mood_id != null ? Number(t.mood_id) : null)),
      playCount: Number(t.play_count) || 0,
      addedAt: o.added_at || o.created_at || '',
    });
  }
  // Newest first.
  out.sort((a, b) => (b.addedAt || '').localeCompare(a.addedAt || ''));
  return out;
}

// ---- Upload: insert track + ownership ----
export interface UploadTrackInput {
  userId: number;
  title: string;
  artist: string;
  album: string;
  durationSeconds: number;
  fileSizeBytes: number;
  fileFormat: string;
  mediaType: 'audio' | 'video';
  storageKey: string;      // idb://… from audioStore.newAudioKey()
  originalFilename: string;
  consentBatchId?: string; // links the track to its mus_legal_acceptance upload_consent
  coverKey?: string;       // idb://… for embedded/manual album art
}

export async function uploadTrack(input: UploadTrackInput): Promise<number> {
  const artistId = await resolveArtistId(input.artist);
  const albumId = await resolveAlbumId(input.album, artistId);
  const now = nowTimestamp();

  const track = await addRow('mus_tracks_all', {
    track_title: input.title,
    artist_id: artistId,
    album_id: albumId,
    duration_seconds: Math.round(input.durationSeconds) || null,
    storage_path: input.storageKey,
    file_format: input.fileFormat,
    file_size_bytes: input.fileSizeBytes,
    media_type: input.mediaType,
    id3_title: input.title,
    id3_artist: input.artist,
    id3_album: input.album,
    // id3_raw carries the consent-batch link + the cover-art blob key.
    id3_raw: (input.consentBatchId || input.coverKey)
      ? { ...(input.consentBatchId ? { consent_batch_id: input.consentBatchId } : {}), ...(input.coverKey ? { cover_key: input.coverKey } : {}) }
      : null,
    play_count: 0,
    is_explicit: 0,
    is_approved: 1,
    is_active: 1, is_deleted: 0,
    created_at: now, updated_at: now,
  }, MUS_TRACK_PK);

  const trackId = Number(track[MUS_TRACK_PK]);

  await addRow('mus_user_owned', {
    user_id: input.userId,
    track_id: trackId,
    is_uploader: 1,
    is_favorite: 0,
    added_at: now,
    upload_source: 'upload',
    original_filename: input.originalFilename,
    is_active: 1, is_deleted: 0,
    created_at: now, updated_at: now,
  }, MUS_OWNED_PK);

  return trackId;
}

// ---- Favorite toggle ----
export async function setFavorite(userOwnedId: number, fav: boolean): Promise<void> {
  await saveRow('mus_user_owned', userOwnedId, {
    is_favorite: fav ? 1 : 0,
    updated_at: nowTimestamp(),
  }, MUS_OWNED_PK);
}

// ---- Delete (soft) ----
// Soft-deletes the ownership row; if the user was the uploader, also soft-deletes
// the catalog track and drops its blob from IndexedDB to reclaim space.
export async function deleteOwnedTrack(view: TrackView, userId: number): Promise<void> {
  const now = nowTimestamp();
  await saveRow('mus_user_owned', view.userOwnedId, { is_deleted: 1, is_active: 0, updated_at: now }, MUS_OWNED_PK);

  const ownedRow = getTableData('mus_user_owned').find((o: any) => o.user_owned_id == view.userOwnedId); // eslint-disable-line eqeqeq
  const wasUploader = ownedRow?.is_uploader == 1; // eslint-disable-line eqeqeq
  // Only purge the catalog track + blob when no other active owner remains.
  const otherOwners = getTableData('mus_user_owned').some((o: any) =>
    // eslint-disable-next-line eqeqeq
    o.track_id == view.trackId && o.user_owned_id != view.userOwnedId &&
    // eslint-disable-next-line eqeqeq
    o.is_deleted != 1 && o.is_active != 0);
  if (wasUploader && !otherOwners) {
    await saveRow('mus_tracks_all', view.trackId, { is_deleted: 1, is_active: 0, updated_at: now }, MUS_TRACK_PK);
    await deleteAudio(view.storageKey);
    if (view.coverKey) await deleteAudio(view.coverKey);
  }
}

// ---- Play history ----
export async function logPlay(userId: number, trackId: number, playlistId: number | null = null): Promise<void> {
  try {
    await addRow('mus_recently_played', {
      user_id: userId,
      track_id: trackId,
      played_at: nowTimestamp(),
      play_source: 'library',
      playlist_id: playlistId,
      device_type: 'web',
      created_at: nowTimestamp(), updated_at: nowTimestamp(),
    }, MUS_RECENT_PK);
  } catch { /* play logging is best-effort — never block playback */ }
}

// ---- Rating (stars) ----
export async function setRating(userOwnedId: number, rating: number): Promise<void> {
  await saveRow('mus_user_owned', userOwnedId, {
    user_rating: rating > 0 ? rating : null,
    updated_at: nowTimestamp(),
  }, MUS_OWNED_PK);
}

// ---- Update song info (title / artist / album) ----
export async function updateTrackInfo(
  trackId: number,
  fields: { title: string; artist: string; album: string },
  coverKey?: string, // set/replace the album-art blob key (omit to leave unchanged)
): Promise<void> {
  const artistId = await resolveArtistId(fields.artist);
  const albumId = await resolveAlbumId(fields.album, artistId);
  const updates: Record<string, any> = {
    track_title: fields.title.trim() || 'Untitled',
    artist_id: artistId,
    album_id: albumId,
    id3_title: fields.title,
    id3_artist: fields.artist,
    id3_album: fields.album,
    updated_at: nowTimestamp(),
  };
  if (coverKey) {
    // Merge into id3_raw so the consent-batch link is preserved.
    const existing = getTableData('mus_tracks_all').find((t: any) => t.track_id == trackId); // eslint-disable-line eqeqeq
    const raw = (existing && existing.id3_raw) ? { ...existing.id3_raw } : {};
    raw.cover_key = coverKey;
    updates.id3_raw = raw;
  }
  await saveRow('mus_tracks_all', trackId, updates, MUS_TRACK_PK);
}

export interface MusicMigrateResult { migrated: number; covers: number; failed: number; skipped: number; total: number; }

/**
 * Migrate this user's device-local (idb://) music blobs UP to the server blob
 * store. Songs uploaded before server storage existed live ONLY in this browser's
 * IndexedDB — run this on the device that has them so a browser wipe can't lose
 * them (CLAUDE.md → File Storage Rules). Idempotent (tracks already on a server
 * key are skipped) + best-effort + non-blocking — safe to call on every Music
 * page open. The local idb blob is kept as a cache (not deleted).
 */
export async function migrateMusicBlobsToServer(userId: number): Promise<MusicMigrateResult> {
  const res: MusicMigrateResult = { migrated: 0, covers: 0, failed: 0, skipped: 0, total: 0 };
  // Need a token to upload — skip silently in localStorage/offline mode.
  if (!localStorage.getItem('akt_auth_token')) return res;
  try { await dbLoadTable('mus_tracks_all'); await dbLoadTable('mus_user_owned'); } catch { /* use cache */ }
  const tracks = getOwnedTracks(userId);
  res.total = tracks.length;
  for (const t of tracks) {
    // Audio blob (the playable file — the important one).
    if (isIdbKey(t.storageKey)) {
      try {
        const blob = await getAudioBlob(t.storageKey);
        if (!blob) { res.skipped++; }
        else {
          const key = await putServerBlobBinary(blob, { scope: 'music', accountOwnerId: userId, ext: t.fileFormat || 'mp3' });
          if (key) { await saveRow('mus_tracks_all', t.trackId, { storage_path: key }, MUS_TRACK_PK); res.migrated++; }
          else res.failed++;
        }
      } catch { res.failed++; }
    }
    // Cover art (non-critical — merged into id3_raw.cover_key).
    if (t.coverKey && isIdbKey(t.coverKey)) {
      try {
        const cb = await getAudioBlob(t.coverKey);
        if (cb) {
          const ck = await putServerBlobBinary(cb, { scope: 'music', accountOwnerId: userId, ext: 'jpg' });
          if (ck) {
            const existing = getTableData('mus_tracks_all').find((x: any) => x.track_id == t.trackId); // eslint-disable-line eqeqeq
            const raw = (existing && existing.id3_raw) ? { ...existing.id3_raw } : {};
            raw.cover_key = ck;
            await saveRow('mus_tracks_all', t.trackId, { id3_raw: raw }, MUS_TRACK_PK);
            res.covers++;
          }
        }
      } catch { /* cover is best-effort */ }
    }
  }
  return res;
}

// ---- Recently played (most-recent first, de-duped to one row per track) ----
export function getRecentlyPlayed(userId: number, limit = 50): TrackView[] {
  const owned = getOwnedTracks(userId);
  const byTrack = new Map<number, TrackView>(owned.map(t => [t.trackId, t]));
  const seen = new Set<number>();
  const out: TrackView[] = [];
  const plays = getTableData('mus_recently_played')
    // eslint-disable-next-line eqeqeq
    .filter((r: any) => r.user_id == userId)
    .sort((a: any, b: any) => (b.played_at || '').localeCompare(a.played_at || ''));
  for (const p of plays) {
    const tid = Number(p.track_id);
    if (seen.has(tid)) continue;
    const t = byTrack.get(tid);
    if (!t) continue;
    seen.add(tid);
    out.push(t);
    if (out.length >= limit) break;
  }
  return out;
}

// ---- Most played (by this user's play count) ----
export function getMostPlayed(userId: number, limit = 50): TrackView[] {
  const owned = getOwnedTracks(userId);
  const byTrack = new Map<number, TrackView>(owned.map(t => [t.trackId, t]));
  const counts = new Map<number, number>();
  for (const r of getTableData('mus_recently_played')) {
    // eslint-disable-next-line eqeqeq
    if (r.user_id != userId) continue;
    const tid = Number(r.track_id);
    if (byTrack.has(tid)) counts.set(tid, (counts.get(tid) || 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tid]) => byTrack.get(tid)!)
    .filter(Boolean);
}

// ---- Grouping helpers (for the "browse by …" tiles) ----
function groupBy(tracks: TrackView[], keyOf: (t: TrackView) => string, labelOf: (k: string) => string): TrackGroup[] {
  const map = new Map<string, TrackView[]>();
  for (const t of tracks) {
    const k = keyOf(t);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(t);
  }
  return Array.from(map.entries())
    .map(([key, ts]) => ({ key, label: labelOf(key), count: ts.length, tracks: ts }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function groupByArtist(tracks: TrackView[]): TrackGroup[] {
  return groupBy(tracks, t => t.artist || 'Unknown Artist', k => k);
}

export function groupByAlbum(tracks: TrackView[]): TrackGroup[] {
  return groupBy(tracks, t => t.album || '(No album)', k => k);
}

export function groupByLanguage(tracks: TrackView[]): TrackGroup[] {
  const langs = getTableData('site_mstr_languages');
  const nameById = new Map<number, string>(langs.map((l: any) => [Number(l.language_id), l.language_name]));
  return groupBy(tracks,
    t => (t.languageId != null ? String(t.languageId) : 'none'),
    k => (k === 'none' ? '(No language)' : (nameById.get(Number(k)) || `Language #${k}`)));
}

export function groupByMood(tracks: TrackView[]): TrackGroup[] {
  const moods = getTableData('mus_mood');
  const nameById = new Map<number, string>(moods.map((m: any) => [Number(m.mood_id), m.mood_name]));
  return groupBy(tracks,
    t => (t.moodId != null ? String(t.moodId) : 'none'),
    k => (k === 'none' ? '(No mood)' : (nameById.get(Number(k)) || `Mood #${k}`)));
}

// ====================================================================
// Wishlist
// ====================================================================
export const MUS_WISHLIST_PK = 'wishlist_id';

export interface WishlistItem {
  wishlistId: number;
  title: string;
  artist: string;
  album: string;
  notes: string;
  priority: number;       // 1 high, 2 normal, 3 low
  status: string;         // wanted | acquired | removed
  addedAt: string;
}

export function getWishlist(userId: number): WishlistItem[] {
  return getTableData('mus_wishlist')
    // eslint-disable-next-line eqeqeq
    .filter((w: any) => w.user_id == userId && w.is_deleted != 1 && w.status !== 'removed')
    .map((w: any) => ({
      wishlistId: Number(w.wishlist_id),
      title: w.title,
      artist: w.artist || '',
      album: w.album || '',
      notes: w.notes || '',
      priority: Number(w.priority) || 2,
      status: w.status || 'wanted',
      addedAt: w.added_at || w.created_at || '',
    }))
    .sort((a, b) => a.priority - b.priority || (b.addedAt || '').localeCompare(a.addedAt || ''));
}

export async function addWishlistItem(
  userId: number,
  fields: { title: string; artist?: string; album?: string; notes?: string; priority?: number },
): Promise<void> {
  await addRow('mus_wishlist', {
    user_id: userId,
    title: fields.title.trim(),
    artist: fields.artist?.trim() || null,
    album: fields.album?.trim() || null,
    notes: fields.notes?.trim() || null,
    priority: fields.priority || 2,
    status: 'wanted',
    added_at: nowTimestamp(),
    is_active: 1, is_deleted: 0,
    created_at: nowTimestamp(), updated_at: nowTimestamp(),
  }, MUS_WISHLIST_PK);
}

export async function setWishlistStatus(wishlistId: number, status: 'wanted' | 'acquired' | 'removed'): Promise<void> {
  await saveRow('mus_wishlist', wishlistId, { status, updated_at: nowTimestamp() }, MUS_WISHLIST_PK);
}

export async function removeWishlistItem(wishlistId: number): Promise<void> {
  await saveRow('mus_wishlist', wishlistId, { is_deleted: 1, status: 'removed', updated_at: nowTimestamp() }, MUS_WISHLIST_PK);
}

// ====================================================================
// Playlists
// ====================================================================
export const MUS_PLAYLIST_PK = 'playlist_id';
export const MUS_PLAYLIST_SONG_PK = 'playlist_song_id';

export interface PlaylistView {
  playlistId: number;
  name: string;
  description: string;
  songCount: number;
}

export interface PlaylistSongView {
  playlistSongId: number;
  sortOrder: number;
  track: TrackView;
}

/** Re-load the playlist tables (call after writes so both API + localStorage modes see fresh data). */
export async function reloadPlaylistTables(): Promise<void> {
  await Promise.all([loadTable('mus_playlist'), loadTable('mus_playlist_song')]);
}

export function getPlaylists(userId: number): PlaylistView[] {
  const songs = getTableData('mus_playlist_song');
  return getTableData('mus_playlist')
    // eslint-disable-next-line eqeqeq
    .filter((p: any) => p.user_id == userId && p.is_deleted != 1)
    .map((p: any) => ({
      playlistId: Number(p.playlist_id),
      name: p.playlist_name,
      description: p.description || '',
      // eslint-disable-next-line eqeqeq
      songCount: songs.filter((s: any) => s.playlist_id == p.playlist_id && s.is_deleted != 1).length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getPlaylistSongs(playlistId: number, userId: number): PlaylistSongView[] {
  const owned = getOwnedTracks(userId);
  const byTrack = new Map<number, TrackView>(owned.map(t => [t.trackId, t]));
  const out: PlaylistSongView[] = [];
  const rows = getTableData('mus_playlist_song')
    // eslint-disable-next-line eqeqeq
    .filter((s: any) => s.playlist_id == playlistId && s.is_deleted != 1)
    .sort((a: any, b: any) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
  for (const s of rows) {
    const track = byTrack.get(Number(s.track_id));
    if (track) out.push({ playlistSongId: Number(s.playlist_song_id), sortOrder: Number(s.sort_order) || 0, track });
  }
  return out;
}

export async function createPlaylist(userId: number, name: string): Promise<number> {
  const row = await addRow('mus_playlist', {
    user_id: userId,
    playlist_name: name.trim() || 'Untitled playlist',
    description: null,
    is_public: 0,
    sort_order: 0,
    is_active: 1, is_deleted: 0,
    created_at: nowTimestamp(), updated_at: nowTimestamp(),
  }, MUS_PLAYLIST_PK);
  return Number(row[MUS_PLAYLIST_PK]);
}

export async function renamePlaylist(playlistId: number, name: string): Promise<void> {
  await saveRow('mus_playlist', playlistId, { playlist_name: name.trim() || 'Untitled playlist', updated_at: nowTimestamp() }, MUS_PLAYLIST_PK);
}

export async function deletePlaylist(playlistId: number): Promise<void> {
  const now = nowTimestamp();
  await saveRow('mus_playlist', playlistId, { is_deleted: 1, is_active: 0, updated_at: now }, MUS_PLAYLIST_PK);
  // Soft-delete its song links too.
  const songs = getTableData('mus_playlist_song').filter((s: any) => s.playlist_id == playlistId && s.is_deleted != 1); // eslint-disable-line eqeqeq
  for (const s of songs) {
    await saveRow('mus_playlist_song', s.playlist_song_id, { is_deleted: 1, updated_at: now }, MUS_PLAYLIST_SONG_PK);
  }
}

/** Add a track to a playlist (no-op if already present). Returns false if it was a dup. */
export async function addTrackToPlaylist(playlistId: number, trackId: number, userId: number): Promise<boolean> {
  const rows = getTableData('mus_playlist_song');
  const dup = rows.find((s: any) => s.playlist_id == playlistId && s.track_id == trackId && s.is_deleted != 1); // eslint-disable-line eqeqeq
  if (dup) return false;
  const maxSort = rows
    .filter((s: any) => s.playlist_id == playlistId && s.is_deleted != 1) // eslint-disable-line eqeqeq
    .reduce((m: number, s: any) => Math.max(m, Number(s.sort_order) || 0), 0);
  await addRow('mus_playlist_song', {
    playlist_id: playlistId,
    track_id: trackId,
    sort_order: maxSort + 10,
    added_by: userId,
    is_active: 1, is_deleted: 0,
    created_at: nowTimestamp(), updated_at: nowTimestamp(),
  }, MUS_PLAYLIST_SONG_PK);
  return true;
}

export async function removeFromPlaylist(playlistSongId: number): Promise<void> {
  await saveRow('mus_playlist_song', playlistSongId, { is_deleted: 1, updated_at: nowTimestamp() }, MUS_PLAYLIST_SONG_PK);
}

/** Move a song up/down by swapping sort_order with its neighbour. */
export async function movePlaylistSong(playlistId: number, playlistSongId: number, dir: 'up' | 'down', userId: number): Promise<void> {
  const songs = getPlaylistSongs(playlistId, userId);
  const idx = songs.findIndex(s => s.playlistSongId === playlistSongId);
  const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
  if (idx < 0 || swapIdx < 0 || swapIdx >= songs.length) return;
  const a = songs[idx], b = songs[swapIdx];
  const now = nowTimestamp();
  await saveRow('mus_playlist_song', a.playlistSongId, { sort_order: b.sortOrder, updated_at: now }, MUS_PLAYLIST_SONG_PK);
  await saveRow('mus_playlist_song', b.playlistSongId, { sort_order: a.sortOrder, updated_at: now }, MUS_PLAYLIST_SONG_PK);
}
